import { useQuery, QueryClient } from "@tanstack/react-query"; // Import QueryClient
import { listingsApi } from "@/lib/api/listings";
import { ListingFilters, ListingType } from "@/lib/types"; // Import ListingFilters and ListingType

interface FeaturedPropertiesHookFilters {
  propertyType?: string[];
  listingType?: ListingType[]; // Add listingType
}

export function useFeaturedProperties(
  filters?: FeaturedPropertiesHookFilters // Accept filters object
) {
  console.log(
    "[useFeaturedProperties] Hook initialized with filters:",
    filters
  ); // Add log
  return useQuery({
    queryKey: ["featuredProperties", filters], // Update queryKey to include filters

    queryFn: async () => {
      try {
        const queryParams: any = {
          featured: true,
          limit: 21, // Fetch up to 9 for 3x3 display
          page: 1,
        };

        if (filters?.propertyType && filters.propertyType.length > 0) {
          queryParams.propertyType = filters.propertyType;
        }

        if (filters?.listingType && filters.listingType.length > 0) {
          queryParams.listing_type = filters.listingType; // Pass listing_type
        }

        console.log("[useFeaturedProperties] Filters received:", filters); // Add log
        console.log(
          "[useFeaturedProperties] Query params sent to API:",
          queryParams
        ); // Add log

        const response = await listingsApi.getListings(queryParams);

        if (
          response &&
          response.status === "success" &&
          Array.isArray(response.data)
        ) {
          return response.data.map((item: any) => ({
            ...item,
            isFeatured: item.is_featured === 1 || Boolean(item.is_featured),
            isBoosted: item.is_boosted === 1 || Boolean(item.is_boosted),
            listingType: item.listing_type,
            location: {
              address: item.address || "",
              city: item.address || "",
            },
          }));
        }

        return [];
      } catch (error) {
        console.error("Error fetching featured properties:", error);
        return [];
      }
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    retry: 1,
  });
}

// Pre-fetch featured properties for faster loads
export function prefetchFeaturedProperties(
  queryClient: QueryClient, // Use QueryClient type
  filters?: FeaturedPropertiesHookFilters // Accept filters object
) {
  queryClient.prefetchQuery({
    queryKey: ["featuredProperties", filters], // Update queryKey to include filters
    queryFn: async () => {
      try {
        const queryParams: any = {
          featured: true,
          limit: 9,
        };

        if (filters?.propertyType && filters.propertyType.length > 0) {
          queryParams.propertyType = filters.propertyType;
        }

        if (filters?.listingType && filters.listingType.length > 0) {
          queryParams.listing_type = filters.listingType; // Pass listing_type
        }

        console.log("[prefetchFeaturedProperties] Filters received:", filters); // Add log
        console.log(
          "[prefetchFeaturedProperties] Query params sent to API:",
          queryParams
        ); // Add log

        const response = await listingsApi.getListings(queryParams);

        if (
          response &&
          response.status === "success" &&
          Array.isArray(response.data)
        ) {
          return response.data.map((item: any) => ({
            ...item,
            isFeatured: item.is_featured === 1 || Boolean(item.is_featured),
            isBoosted: item.is_boosted === 1 || Boolean(item.is_boosted),
            listingType: item.listing_type,
            location: {
              address: item.address || "",
              city: item.address || "",
            },
          }));
        }

        return [];
      } catch (error) {
        console.error("Error pre-fetching featured properties:", error);
        return [];
      }
    },
  });
}
